import React, { useEffect, useMemo, useRef, useState } from "react";

// 日別の推移（閲覧数・訪問者数）。同じ単位（回数）なので 1 本の縦軸で描く

export interface TrendSeries {
  key: string;
  label: string;
  color: string; // CSS 変数（var(--series-1) など）
  values: number[];
}

interface Props {
  dates: string[]; // YYYY-MM-DD
  series: TrendSeries[];
}

const PLOT_H = 200;
const AXIS_H = 24;
const PAD = { top: 12, right: 56, left: 44 };

// 目盛りをきりのよい値にする
function niceMax(v: number): number {
  if (v <= 4) return 4;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (m * pow >= v) return m * pow;
  }
  return 10 * pow;
}

const fmt = (n: number) => n.toLocaleString("ja-JP");
const shortDate = (d: string) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;

export const TrendChart: React.FC<Props> = ({ dates, series }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(320, entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = dates.length;
  const max = niceMax(Math.max(1, ...series.flatMap((s) => s.values)));
  const plotW = width - PAD.left - PAD.right;
  const x = (i: number) => PAD.left + (n <= 1 ? plotW / 2 : (i * plotW) / (n - 1));
  const y = (v: number) => PAD.top + PLOT_H - (v / max) * PLOT_H;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * max);
  const xTickEvery = Math.max(1, Math.ceil(n / 7));

  const paths = useMemo(
    () =>
      series.map((s) => s.values.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, width, max, n],
  );

  // 右端の値ラベルは、近すぎて重なるときは出さない（凡例・ツールチップ・表で読める）
  const endLabels = useMemo(() => {
    if (n === 0) return [];
    const ys = series.map((s) => y(s.values[n - 1] ?? 0));
    const apart = ys.every((a, i) => ys.every((b, j) => i === j || Math.abs(a - b) >= 14));
    return apart ? series.map((s, i) => ({ y: ys[i], value: s.values[n - 1] ?? 0, key: s.key })) : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, width, max, n]);

  const indexAt = (clientX: number) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || n === 0) return null;
    const px = clientX - rect.left - PAD.left;
    return Math.min(n - 1, Math.max(0, Math.round((px / plotW) * (n - 1))));
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") setHover((h) => Math.min(n - 1, (h ?? -1) + 1));
    else if (e.key === "ArrowLeft") setHover((h) => Math.max(0, (h ?? n) - 1));
    else if (e.key === "Escape") setHover(null);
  };

  return (
    <div className="an-trend">
      <ul className="an-legend" aria-label="凡例">
        {series.map((s) => (
          <li key={s.key}>
            <svg width="16" height="8" aria-hidden="true">
              <line x1="0" y1="4" x2="16" y2="4" stroke={s.color} strokeWidth="2" strokeLinecap="round" />
            </svg>
            {s.label}
          </li>
        ))}
      </ul>
      <div
        ref={wrapRef}
        className="an-trend-plot"
        onPointerMove={(e) => setHover(indexAt(e.clientX))}
        onPointerLeave={() => setHover(null)}
      >
        <svg
          width={width}
          height={PAD.top + PLOT_H + AXIS_H}
          role="img"
          aria-label={`日別の${series.map((s) => s.label).join("と")}（←→キーで日付を移動）`}
          tabIndex={0}
          onKeyDown={onKey}
          onBlur={() => setHover(null)}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} className={t === 0 ? "an-axis" : "an-grid"} />
              <text x={PAD.left - 8} y={y(t)} className="an-tick" textAnchor="end" dominantBaseline="middle">
                {fmt(t)}
              </text>
            </g>
          ))}
          {dates.map((d, i) =>
            i % xTickEvery === 0 || i === n - 1 ? (
              <text key={d} x={x(i)} y={PAD.top + PLOT_H + 16} className="an-tick" textAnchor="middle">
                {shortDate(d)}
              </text>
            ) : null,
          )}
          {series.map((s, si) => (
            <path key={s.key} d={paths[si]} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {/* 右端の点と値 */}
          {n > 0 &&
            series.map((s) => (
              <circle key={s.key} cx={x(n - 1)} cy={y(s.values[n - 1] ?? 0)} r="4" fill={s.color} stroke="var(--surface-1)" strokeWidth="2" />
            ))}
          {endLabels.map((l) => (
            <text key={l.key} x={x(n - 1) + 8} y={l.y} className="an-endlabel" dominantBaseline="middle">
              {fmt(l.value)}
            </text>
          ))}
          {hover !== null && (
            <g pointerEvents="none">
              <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + PLOT_H} className="an-crosshair" />
              {series.map((s) => (
                <circle key={s.key} cx={x(hover)} cy={y(s.values[hover] ?? 0)} r="4" fill={s.color} stroke="var(--surface-1)" strokeWidth="2" />
              ))}
            </g>
          )}
        </svg>
        {hover !== null && (
          <div
            className="an-tooltip"
            style={{ left: Math.min(x(hover) + 12, width - 160), top: PAD.top }}
            role="status"
          >
            <div className="an-tooltip-title">{dates[hover]}</div>
            {series.map((s) => (
              <div key={s.key} className="an-tooltip-row">
                <svg width="12" height="8" aria-hidden="true">
                  <line x1="0" y1="4" x2="12" y2="4" stroke={s.color} strokeWidth="2" strokeLinecap="round" />
                </svg>
                <strong>{fmt(s.values[hover] ?? 0)}</strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <details className="an-tableview">
        <summary>表で見る</summary>
        <table className="an-table">
          <thead>
            <tr>
              <th>日付</th>
              {series.map((s) => (
                <th key={s.key} className="num">
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dates.map((d, i) => (
              <tr key={d}>
                <td>{d}</td>
                {series.map((s) => (
                  <td key={s.key} className="num">
                    {fmt(s.values[i] ?? 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
};
